/**
 * HM-422 cluster 21 Clienti CRM: pagina /ospiti con lista paginata,
 * ricerca debounced, toggle VIP, drawer dettaglio.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  type DuplicateGroup,
  type GuestListItem,
  listDuplicates,
  listGuests,
  patchGuest,
} from '../../api/guests-crm.js';
import { GuestDetailDrawer } from './GuestDetailDrawer.js';

function formatItalianDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}

export function OspitiPage() {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput, 300);
  const [page, setPage] = useState(1);
  const [vipOnly, setVipOnly] = useState(false);
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  // Reset pagina su cambio ricerca/filtro VIP.
  useEffect(() => {
    setPage(1);
  }, []);

  const listQuery = useQuery({
    queryKey: ['guests', 'list', debouncedSearch, page, vipOnly],
    queryFn: () =>
      listGuests({
        search: debouncedSearch.length >= 2 ? debouncedSearch : undefined,
        page,
        pageSize: 25,
        vipOnly,
      }),
    staleTime: 30_000,
  });

  const duplicatesQuery = useQuery<{ duplicates: DuplicateGroup[] }>({
    queryKey: ['guests', 'duplicates'],
    queryFn: listDuplicates,
    staleTime: 5 * 60_000,
  });

  const vipToggleMutation = useMutation({
    mutationFn: (vars: { id: string; isVip: boolean }) =>
      patchGuest(vars.id, { isVip: vars.isVip }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guests', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['guests', 'detail'] });
    },
  });

  const duplicatePhones = useMemo(() => {
    const set = new Set<string>();
    for (const g of duplicatesQuery.data?.duplicates ?? []) {
      if (g.phone) set.add(g.phone);
    }
    return set;
  }, [duplicatesQuery.data]);

  const items: GuestListItem[] = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = listQuery.data?.totalPages ?? 1;

  return (
    <div className="ospiti-root" data-testid="ospiti-page" style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: '#3D2F24', margin: 0 }}>Clienti</h1>
          <p style={{ fontSize: 13, color: '#7A6957', margin: '4px 0 0 0' }}>
            {total} {total === 1 ? 'cliente' : 'clienti'} in archivio
            {duplicatePhones.size > 0 && (
              <span
                style={{ marginLeft: 12, color: '#E97B3D', fontWeight: 600 }}
                data-testid="ospiti-duplicates-warning"
              >
                ⚠ {duplicatePhones.size} possibili duplicati (stesso telefono)
              </span>
            )}
          </p>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          alignItems: 'center',
        }}
      >
        <input
          type="search"
          placeholder="Cerca per nome, telefono o email…"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            setPage(1);
          }}
          data-testid="ospiti-search-input"
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #D4C4B0',
            fontSize: 14,
          }}
        />
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: '#3D2F24',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={vipOnly}
            onChange={(e) => {
              setVipOnly(e.target.checked);
              setPage(1);
            }}
            data-testid="ospiti-vip-filter"
          />
          Solo VIP
        </label>
      </div>

      <div
        style={{
          border: '1px solid #E8DECF',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead style={{ background: '#FAF7F2' }}>
            <tr>
              <th style={th}>Nome</th>
              <th style={th}>Telefono</th>
              <th style={th}>Email</th>
              <th style={{ ...th, textAlign: 'right' }}>Soggiorni</th>
              <th style={th}>Ultimo</th>
              <th style={{ ...th, textAlign: 'right' }}>Totale</th>
              <th style={{ ...th, width: 60 }}>VIP</th>
            </tr>
          </thead>
          <tbody>
            {listQuery.isLoading && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}>
                  Caricamento…
                </td>
              </tr>
            )}
            {!listQuery.isLoading && items.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#7A6957' }}>
                  Nessun cliente trovato.
                </td>
              </tr>
            )}
            {items.map((g) => {
              const isDup = !!g.phone && duplicatePhones.has(g.phone);
              return (
                <tr
                  key={g.id}
                  style={{ borderTop: '1px solid #F0E8DA', cursor: 'pointer' }}
                  onClick={() => setSelectedGuestId(g.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedGuestId(g.id);
                    }
                  }}
                  tabIndex={0}
                  data-testid={`ospiti-row-${g.id}`}
                >
                  <td style={td}>
                    <strong>
                      {g.lastName} {g.firstName}
                    </strong>
                    {isDup && (
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: '#FFF3E6',
                          color: '#E97B3D',
                          fontSize: 11,
                        }}
                        data-testid="ospiti-dup-badge"
                        title="Stesso telefono di altro cliente"
                      >
                        DUP
                      </span>
                    )}
                  </td>
                  <td style={td}>{g.phone ?? '—'}</td>
                  <td style={td}>{g.email ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{g.staysCount}</td>
                  <td style={td}>{formatItalianDate(g.lastStayAt)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>€ {Number(g.totalSpent).toFixed(2)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        vipToggleMutation.mutate({ id: g.id, isVip: !g.isVip });
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 18,
                        color: g.isVip ? '#E97B3D' : '#C0B8A8',
                      }}
                      data-testid={`ospiti-vip-toggle-${g.id}`}
                      aria-label={g.isVip ? 'Rimuovi VIP' : 'Imposta VIP'}
                    >
                      {g.isVip ? '★' : '☆'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            marginTop: 16,
          }}
          data-testid="ospiti-pagination"
        >
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="ospiti-page-prev"
          >
            ← Precedente
          </button>
          <span style={{ fontSize: 13, color: '#7A6957' }}>
            Pagina {page} di {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="ospiti-page-next"
          >
            Successiva →
          </button>
        </div>
      )}

      {selectedGuestId && (
        <GuestDetailDrawer
          guestId={selectedGuestId}
          onClose={() => setSelectedGuestId(null)}
          onVipToggle={(isVip) => vipToggleMutation.mutate({ id: selectedGuestId, isVip })}
        />
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const td: React.CSSProperties = {
  padding: '12px 14px',
  verticalAlign: 'middle',
};
